import { useMemo } from 'react'
import type { ProjectSummary } from '../lib/db'
import { money as fmt } from '../lib/quote'
import { today, type JobFacts, type Todo } from '../lib/today'
import { Figures, Loading, type Figure } from './bits'

import Stops from './Stops'
import { IconBell, IconHouse, IconMoney, IconRuler } from './icons'

/**
 * THE MORNING, NOT THE GALLERY.
 *
 * Signing in used to land on forty photographs of houses, newest first, which
 * is a lovely thing to look at and answers no question anybody has at seven in
 * the morning. This answers one: what needs doing, in the order it needs
 * doing, each row naming the house so nobody has to open one to find out.
 *
 * THE SHAPE IS THE HOME SCREEN FROM THE DESIGN FILE. Four figure cards across
 * the top, then the work on the left and the standing context on the right.
 * Three things were wrong with the first pass and are fixed here:
 *
 *   The word Today appeared three times on one screen - in the sidebar, in the
 *   header trail, and again as an eyebrow over the title. The eyebrow is gone.
 *
 *   Every row carried its own Review button, so the page was a column of five
 *   identical buttons under two more in the header. A row is the button now.
 *
 *   All jobs floated on the right of the header, vertically centered against a
 *   three line title block and aligned to nothing. It sits with the list it
 *   belongs to.
 *
 * UNKNOWN IS NOT ZERO. TOD-01 also wants crew status, weather, leads and
 * material shortages. None of those exist in this product yet, and a card
 * reading "0 crews late" would be lying in the most expensive way a dashboard
 * can, by looking answered. They are listed as not covered, with what would
 * have to be built, and they come off that list when they ship.
 *
 * Every number here is computed off jobs the person has already saved. No
 * network, no clock service, no model.
 */

const DAY = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
})

export default function Today({
  items,
  onChanged,
  onOpenJob,
  onSeeAll,
  onSeeMoney,
  onSeeDesign,
  onSeeWeek,
  onSeeEstimates,
}: {
  items: ProjectSummary[] | null
  /** Starting or finishing a visit writes to the job, so the list is re-read. */
  onChanged: () => void
  onOpenJob: (job: ProjectSummary) => void
  onSeeAll: () => void
  /*
    EVERY FIGURE WITH A REAL DESTINATION GETS ONE. The reference card always
    carries a way in, and three of these four have somewhere true to go: the
    jobs list, the money screen, and the step where a length gets walked. The
    fourth's answer is the list six inches below it, so it moves you there
    rather than to another page.
  */
  onSeeMoney: () => void
  onSeeDesign: () => void
  /** The week, from Today's stops. The same destination the sidebar names. */
  onSeeWeek?: () => void
  /*
    OWED AND OUT ARE TWO DESTINATIONS. Still owed goes to Invoices and Waiting
    on an answer goes to Estimates: October and January are different questions
    and they were sharing one link.
  */
  onSeeEstimates?: () => void
}) {
  const t = useMemo(() => {
    const facts: JobFacts[] = (items ?? []).map((j) => ({
      id: j.id,
      name: j.name,
      customer: j.customer,
      address: j.address,
      savedAt: j.savedAt,
      unmeasuredFeet: j.work?.unmeasuredFeet ?? 0,
      missingLengths: j.work?.missingLengths ?? 0,
      driftCount: j.work?.driftCount ?? 0,
      balance: j.money?.balance ?? 0,
      hasPaper: !!j.money && j.money.total > 0,
      invoiced: j.money?.kind === 'invoice',
    }))
    return today(facts, (n) => fmt(n))
  }, [items])

  const open = (todo: Todo) => {
    const job = (items ?? []).find((j) => j.id === todo.jobId)
    if (job) onOpenJob(job)
  }

  if (items === null) {
    // Nothing is asserted while the jobs are still being read. An empty state
    // that turns out to be wrong a moment later is worse than a blank.
    return <Loading what="Reading your jobs" />
  }

  const jobs = items ?? []

  /*
   * THE FOUR FIGURES, AND EVERY ONE OF THEM COMPUTED.
   *
   * Not one of these is a placeholder. Feet not walked is a sum over included
   * sections; owed is a sum over jobs that actually have paper on them. A
   * figure card is worth the space it takes only when the number under it is
   * true, which is why there are four of them and not eight.
   */
  const unwalked = Math.round(
    jobs.reduce((n, j) => n + (j.work?.unmeasuredFeet ?? 0), 0),
  )
  /*
   * A QUOTE IS NOT OWED, AND A LOST JOB OWES NOTHING.
   *
   * This summed the balance on every job carrying paper, which put four
   * quotes nobody has answered into the same number as one real invoice and
   * made Still owed the largest and least true figure on the screen. Money is
   * owed when a person has been invoiced for it. Everything else is money that
   * might arrive, which is the question Estimates answers, not this one.
   */
  const invoices = jobs.filter((j) => j.money?.kind === 'invoice' && j.money.balance > 0)
  const owed = invoices.reduce((n, j) => n + (j.money?.balance ?? 0), 0)
  const owedWho =
    invoices.length === 0
      ? 'Nothing outstanding'
      : invoices.length === 1
        ? `One invoice, ${invoices[0].customer || 'no name yet'}`
        : `${invoices.length} invoices`

  /*
   * NOT WALKED NAMES THE SECTION AND THE HOUSE. A number of feet with no
   * subject is a number you cannot act on; the whole point of the figure is
   * that somebody gets in a truck.
   */
  const unwalkedJobs = jobs.filter((j) => (j.work?.unmeasuredFeet ?? 0) > 0)
  const unwalkedWho =
    unwalkedJobs.length === 0
      ? 'Every length verified'
      : unwalkedJobs.length === 1
        ? `One section, ${unwalkedJobs[0].customer || 'no name yet'}`
        : `${unwalkedJobs.length} jobs`
  const approvedJobs = jobs.filter((j) => j.work?.approved).length
  const measured = jobs.filter(
    (j) => (j.work?.unmeasuredFeet ?? 0) === 0 && (j.work?.missingLengths ?? 0) === 0 && j.layers > 0,
  ).length

  const figures: Figure[] = [
    {
      label: 'Jobs saved',
      value: String(jobs.length),
      unit: '',
      tone: 1,
      icon: <IconHouse size={17} />,
      note: jobs.length === 0 ? 'Start a house to begin' : `${measured} fully measured`,
      action: jobs.length > 0 ? { label: 'View all jobs', onClick: onSeeAll } : undefined,
    },
    {
      label: 'Needs attention',
      value: String(t.todos.length),
      unit: '',
      tone: 2,
      icon: <IconBell size={17} />,
      note: t.todos.length === 0 ? 'Nothing waiting on you' : 'Listed below, worst first',
      action:
        t.todos.length > 0
          ? {
              label: 'See the list',
              onClick: () => {
                const first = document.querySelector<HTMLButtonElement>('.tod-row')
                first?.scrollIntoView({ block: 'center', behavior: 'smooth' })
                first?.focus()
              },
            }
          : undefined,
    },
    {
      label: 'Still owed',
      value: fmt(owed),
      unit: '',
      tone: 3,
      icon: <IconMoney size={17} />,
      note: owedWho,
      /* Owed is what the invoices list is for, and it is one press away. */
      action: { label: 'See the invoices', onClick: onSeeMoney },
    },
    {
      // Every card in a row of equals gets the same treatment. This one was
      // left plain while its three neighbours were tinted, and a row of three
      // and a gap is what you notice instead of the numbers.
      label: 'Not walked',
      value: String(unwalked),
      unit: 'ft',
      tone: 4,
      icon: <IconRuler size={17} />,
      note: unwalkedWho,
      action: { label: 'Go and measure', onClick: onSeeDesign },
    },
  ]

  /*
   * THE THREE MILESTONES, FROM THE PROTOTYPE, AND EVERY ONE OF THEM REAL.
   *
   * Verify, approve, reserve. The first two are computed off saved jobs and
   * tick on their own when the work is actually done. The third cannot tick,
   * because there is no stock behind it, and it says so rather than sitting
   * there as a checkbox nobody can ever satisfy.
   */
  /*
   * WAITING IS A QUOTE THAT WENT OUT AND HAS NOT COME BACK. Oldest first,
   * because the head of this list is who to call. A lost job is not waiting on
   * anything, and an invoice is not a quote.
   */
  const DAYMS = 86400000
  const waiting = jobs
    .filter((j) => j.money?.kind === 'quote' && (j.money?.total ?? 0) > 0 && j.status === 'quoted')
    .map((j) => {
      const days = Math.max(
        0,
        Math.round((Date.now() - new Date(j.money?.dated ?? j.savedAt).getTime()) / DAYMS),
      )
      const age =
        days >= 21
          ? `${Math.round(days / 7)} weeks`
          : days === 1
            ? '1 day'
            : `${days} days`
      const un = Math.round(j.work?.unmeasuredFeet ?? 0)
      return { job: j, days, said: un > 0 ? `${age}. ${un} ft not walked.` : age }
    })
    .sort((a, b) => b.days - a.days)
  if (waiting.length > 0) waiting[0].said = `${waiting[0].said.replace(/\.$/, '')}, the oldest. Call first.`
  const waitingTotal = waiting.reduce((n, w) => n + (w.job.money?.total ?? 0), 0)

  /* Priced means somebody has put a number on a job. See Getting started. */
  const anyPriced = jobs.some((j) => (j.money?.total ?? 0) > 0)

  const milestones = [
    { done: measured > 0, label: 'Verify sections and quantities', why: '' },
    { done: approvedJobs > 0, label: 'Review and approve a package', why: '' },
    /*
     * THE THIRD STEP IS THE ONE A NEW ACCOUNT ACTUALLY CANNOT DO. It was
     * Reserve materials, which can never tick because no stock sits behind it,
     * so the card shipped with a step that was permanently unfinished. The
     * price per foot ships as a placeholder and is treated as unset, and until
     * somebody finds Settings and types a number this account cannot produce a
     * priced quote at all. Nothing else in the product says so.
     */
    { done: anyPriced, label: 'Set a price per foot', why: '' },
  ]
  const doneCount = milestones.filter((m) => m.done).length

  return (
    <>
      {/*
        One line of identity and one line of fact. The destination is already
        named in the sidebar and again in the trail above this, so naming it a
        third time here was the loudest thing on the screen saying nothing.
      */}
      <div className="ws-head">
        <div>
          {/*
            THE DATE, AND ONE LINE THAT POINTS AT THE WORK.

            It said Welcome back, which is true on any morning of any season and
            tells nobody whether to open the app. The design package puts the
            date here instead: this screen is the morning, and the morning has
            a name. The line under it says where to start rather than restating
            a figure that is already three inches below in its own card.
          */}
          <h1>{DAY.format(new Date())}</h1>
          <p>
            {jobs.length === 0
              ? 'No jobs saved yet. Start with a photograph.'
              : `${jobs.length} ${jobs.length === 1 ? 'job' : 'jobs'} saved. Start with the list on the left.`}
          </p>
        </div>
      </div>

      <Figures items={figures} />

      {/*
        WHERE YOU ARE GOING TODAY, ABOVE THE REST OF THE MORNING.

        TOD-01 asks for today's visits on this page and TOD-02 is the crew's
        view of the same thing. There is one login and no roles here, so a
        separate Crew Today would be a duplicate screen nobody could be the
        crew on. It renders nothing at all on a day with no stops, which is
        most days in July. See ui/Stops.tsx.
      */}
      {/*
        GETTING STARTED IS A FIRST RUN CARD, NOT A PERMANENT ONE.

        It sat on every screen forever, and its third milestone was Reserve
        materials, which cannot ever tick because there is no stock behind it:
        a checklist with a step nobody can complete reads as a product that is
        not finished. It shows only while nothing has been priced, which is the
        one moment it is true, and the step it names is the thing a brand new
        account actually cannot do until somebody finds Settings.
      */}
      {jobs.length > 0 && !anyPriced && (
        <div className="card pad tod-first">
          <p className="ws-eyebrow">Getting started</p>
          <h2>
            {doneCount} of {milestones.length} milestones complete
          </h2>
          <div className="learning-progress">
            <i style={{ width: `${(doneCount / milestones.length) * 100}%` }} />
          </div>
          <div className="tod-miles">
            {milestones.map((m) => (
              <span key={m.label} className={m.done ? 'done' : ''}>
                <i aria-hidden="true">{m.done ? '\u2713' : '\u25cb'}</i>
                {m.label}
                {m.why && <em>{m.why}</em>}
              </span>
            ))}
          </div>
        </div>
      )}

      {/*
        FOUR PANELS, TWO BY TWO, EVERY ONE OF THEM A LIST.

        The work on the left, the day on the right, then the money out and what
        this screen cannot see. Rows stretch, so the two panels in a row share a
        top and a bottom rather than leaving a ragged gutter down the middle.
      */}
      <div className="tod-cols">
        <div className="card ws-p tod-todo">
          <div className="ws-ph">
            <h3>What to do first</h3>
            <button className="link-btn" onClick={onSeeAll}>
              All jobs
            </button>
          </div>
          {jobs.length === 0 ? (
            <p className="ws-p-empty">
              Start a house and it shows up here with whatever it still needs.
            </p>
          ) : t.todos.length === 0 ? (
            <p className="ws-p-empty">Every job has its lengths and its scope in order.</p>
          ) : (
            t.todos.map((row, i) => (
              /*
                THE ROW IS THE BUTTON. Five identical Review buttons down the
                page told a person nothing about which one to press, and cost
                the width of a word each. The whole row opens the job, which is
                the only thing any of those buttons ever did. The number is the
                rank, and the rank is the entire point of this card.
              */
              <button
                key={`${row.kind}-${row.jobId}`}
                type="button"
                className={`tod-row ${row.kind}`}
                onClick={() => open(row)}
              >
                <span className="tod-pos">{i + 1}</span>
                <span className="tod-where">{row.where}</span>
                <span className="tod-said">{row.said}</span>
                <span className="tod-go" aria-hidden="true">
                  &rsaquo;
                </span>
              </button>
            ))
          )}
        </div>

        {/*
          TOD-01 asks for today's visits on this page and TOD-02 is the crew's
          view of the same thing. There is one login and no roles here, so a
          separate Crew Today would be a duplicate screen nobody could be the
          crew on. It renders nothing at all on a day with no stops, which is
          most days in July. See ui/Stops.tsx.
        */}
        <Stops
          items={jobs}
          onChanged={onChanged}
          onOpenJob={onOpenJob}
          compact
          onSeeWeek={onSeeWeek}
        />

        {/*
          WHO HAS NOT ANSWERED, OLDEST FIRST.

          The same question Estimates answers in October, cut to the two that
          matter this morning. The footer states the total out and the standing
          truth about the word sent, because nothing in this app has ever sent
          a message and a list of quotes marked sent implies otherwise.
        */}
        {waiting.length > 0 && (
          <div className="card ws-p tod-wait">
            <div className="ws-ph">
              <h3>Waiting on an answer</h3>
              <button className="link-btn" onClick={onSeeEstimates}>
                Estimates
              </button>
            </div>
            {waiting.slice(0, 4).map((w) => (
              <div key={w.job.id} className="tod-wrow">
                <div>
                  <b>{w.job.customer || 'No name yet'}</b>
                  <small>{w.said}</small>
                </div>
                <span className="tod-wamt">{fmt(w.job.money?.total ?? 0)}</span>
                <button
                  type="button"
                  className="btn btn-quiet btn-sm"
                  onClick={() => onOpenJob(w.job)}
                >
                  Open
                </button>
              </div>
            ))}
            <p className="ws-pfoot">
              {fmt(waitingTotal)} out on {waiting.length}{' '}
              {waiting.length === 1 ? 'quote' : 'quotes'}. Sent means you marked it sent.
            </p>
          </div>
        )}

        {/*
          SAID OUT LOUD, RATHER THAN LEFT OFF THE SCREEN.

          Leaving these out would be tidier and would let somebody believe this
          screen had checked them. Each line names what would have to be built,
          in full, as a two column list. It is never a tooltip and never a
          footnote, because a thing this screen cannot see is content.
        */}
        <div className="card ws-p tod-unknown">
          <div className="ws-ph">
            <h3>Not covered yet</h3>
          </div>
          <ul className="ws-dk">
            {t.notKnown.map((n) => (
              <li key={n.label}>
                <b>{n.label}</b>
                <span>{n.because}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  )
}
